import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useRef } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Button, Card } from '@/components/admin/ui';
import {
  BarList,
  ChartCard,
  EmptyState,
  KpiTile,
  RangePicker,
} from '@/components';
import type { AppVersionsAnalyticsResponse, Range } from '@/lib/admin-types';
import { PRIMARY_HEX } from '@/lib/colors';
import { formatInt } from '@/lib/format';
import { apiJson } from '@/lib/api';

/** Keep range switches and revisits snappy; server also caches ~5 minutes. */
const APP_VERSIONS_STALE_MS = 5 * 60_000;

const HINTS = {
  kpiOnLatest:
    'Share of devices with last_seen in the selected window whose registered app_version is ≥ the configured latest for that platform (Config → App Version).',
  kpiMedian:
    'Median days between a device’s last sighting on an older semver and its first sighting on a newer semver, for upgrades that completed in this window (resource taps, set completions, and game runs; look back 30 days before the window).',
  kpiP90:
    '90th percentile of the same upgrade lag distribution as median days to upgrade.',
  kpiUpgrades:
    'Count of observed consecutive semver upgrades (old → newer) completing in the window, from resource taps, set completions, and game runs (flashcard views excluded).',
  onLatest:
    'Among devices with at least one resource tap, set completion, or game run that Pacific day, share whose event version is ≥ configured latest for their platform. Split by iOS / Android / all.',
  versionShare:
    'Daily share of active event devices on each app_version (top versions in the window; remainder rolled into “other”). Based on resource taps, set completions, and game runs.',
  distribution:
    'Devices with last_seen in the window, grouped by registered app_version (current install, not historical events).',
  behind:
    'How far registered app_version sits behind configured latest (on latest / 1 behind / 2+ behind). Requires latest thresholds in Config → App Version.',
  lagBuckets:
    'Histogram of days from last pre-upgrade event to first post-upgrade event for observed upgrades in the window.',
} as const;

const VERSION_COLORS = [
  PRIMARY_HEX,
  '#0ea5e9',
  '#f97316',
  '#8b5cf6',
  '#ec4899',
  '#14b8a6',
  '#eab308',
  '#64748b',
  '#ef4444',
  '#22c55e',
];

type Search = { range: Range };

function fmtDay(iso: string) {
  const [, m, day] = iso.split('-');
  return `${m}/${day}`;
}

function formatPct(value: number | null | undefined, digits = 0): string {
  if (value == null || Number.isNaN(value)) return '—';
  const pct = value * 100;
  return digits > 0 ? `${pct.toFixed(digits)}%` : `${Math.round(pct)}%`;
}

function formatDays(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return value < 10 ? `${value.toFixed(1)}d` : `${Math.round(value)}d`;
}

function versionLabel(key: string) {
  return key === 'unknown' ? 'Unknown' : key === 'other' ? 'Other' : key;
}

function AppVersionsPage() {
  const { range } = Route.useSearch();
  const navigate = useNavigate({ from: '/admin/app-versions' });
  const qc = useQueryClient();
  const forceRefreshRef = useRef(false);

  const q = useQuery({
    queryKey: ['admin', 'analytics', 'app-versions', range],
    queryFn: () => {
      const refresh = forceRefreshRef.current;
      forceRefreshRef.current = false;
      const qs = new URLSearchParams({ range });
      if (refresh) qs.set('refresh', '1');
      return apiJson<AppVersionsAnalyticsResponse>(
        `/v1/admin/analytics/app-versions?${qs.toString()}`
      );
    },
    staleTime: APP_VERSIONS_STALE_MS,
    placeholderData: (previousData) => previousData,
  });

  const data = q.data;
  const summary = data?.summary;
  const versions = data?.versions ?? [];
  // Keep prior range visible while the next range loads (placeholderData).
  const loading = q.isLoading && !data;

  const onLatestRows = useMemo(
    () =>
      (data?.on_latest_days ?? []).map((d) => ({
        label: fmtDay(d.date),
        all: d.all == null ? null : Math.round(d.all * 1000) / 10,
        ios: d.ios == null ? null : Math.round(d.ios * 1000) / 10,
        android: d.android == null ? null : Math.round(d.android * 1000) / 10,
        active_devices: d.active_devices,
      })),
    [data?.on_latest_days]
  );

  const shareRows = useMemo(
    () =>
      (data?.version_share_days ?? []).map((d) => {
        const row: Record<string, string | number> = { label: fmtDay(d.date) };
        for (const ver of versions) {
          row[ver] = Math.round((d.shares[ver] ?? 0) * 1000) / 10;
        }
        return row;
      }),
    [data?.version_share_days, versions]
  );

  const hasOnLatest = onLatestRows.some((r) => (r.active_devices ?? 0) > 0);
  const hasShare = shareRows.some((r) =>
    versions.some((v) => Number(r[v] ?? 0) > 0)
  );
  const hasDist = (data?.distribution.all.length ?? 0) > 0;
  const hasLag = (data?.upgrade_lag.count ?? 0) > 0;

  const latestLabel = [
    data?.latest_by_platform.ios
      ? `iOS ${data.latest_by_platform.ios}`
      : null,
    data?.latest_by_platform.android
      ? `Android ${data.latest_by_platform.android}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-nova-foreground">
            App Versions
          </h1>
          <p className="mt-1 text-sm text-nova-muted-foreground">
            Upgrade speed and version mix across active installs
            {latestLabel ? (
              <>
                {' '}
                · configured latest: <span className="font-medium text-nova-foreground">{latestLabel}</span>
              </>
            ) : (
              <> · set latest in Config → App Version for “on latest” metrics</>
            )}
            .
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <RangePicker
            value={range}
            options={['24h', '7d', '30d', '90d']}
            onChange={(r) => {
              void navigate({ search: { range: r } });
            }}
          />
          <Button
            variant="secondary"
            size="sm"
            disabled={q.isFetching}
            onClick={() => {
              forceRefreshRef.current = true;
              void qc.invalidateQueries({
                queryKey: ['admin', 'analytics', 'app-versions'],
              });
            }}
          >
            {q.isFetching ? 'Refreshing…' : 'Refresh'}
          </Button>
        </div>
      </div>

      {q.error && (
        <p className="text-sm text-red-600">
          {(q.error as Error).message}
          <button
            type="button"
            className="ml-2 underline"
            onClick={() => void q.refetch()}
          >
            Retry
          </button>
        </p>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiTile
          label="% on latest"
          value={formatPct(summary?.on_latest_pct)}
          loading={loading}
          tooltip={HINTS.kpiOnLatest}
        />
        <KpiTile
          label="Median days to upgrade"
          value={formatDays(summary?.median_days_to_upgrade)}
          loading={loading}
          tooltip={HINTS.kpiMedian}
        />
        <KpiTile
          label="P90 days to upgrade"
          value={formatDays(summary?.p90_days_to_upgrade)}
          loading={loading}
          tooltip={HINTS.kpiP90}
        />
        <KpiTile
          label="Upgrades observed"
          value={summary?.upgrades_observed ?? 0}
          loading={loading}
          tooltip={HINTS.kpiUpgrades}
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiTile
          label="Active devices"
          value={summary?.active_devices ?? 0}
          loading={loading}
          tooltip="Devices with last_seen in the selected Pacific window."
        />
        <KpiTile
          label="On latest · iOS"
          value={formatPct(summary?.on_latest_ios_pct)}
          loading={loading}
          tooltip={`${formatInt(summary?.active_ios ?? 0)} active iOS devices. ${HINTS.kpiOnLatest}`}
        />
        <KpiTile
          label="On latest · Android"
          value={formatPct(summary?.on_latest_android_pct)}
          loading={loading}
          tooltip={`${formatInt(summary?.active_android ?? 0)} active Android devices. ${HINTS.kpiOnLatest}`}
        />
        <KpiTile
          label="Versions in use"
          value={summary?.versions_in_use ?? 0}
          loading={loading}
          tooltip="Distinct parseable app versions seen on devices or events in the window."
        />
      </div>

      <ChartCard
        title="% of daily actives on latest"
        tooltip={HINTS.onLatest}
        height={300}
        loading={loading}
        error={q.error ? String(q.error) : undefined}
      >
        {!loading && !hasOnLatest ? (
          <EmptyState title="No event data in window" />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={onLatestRows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis
                domain={[0, 100]}
                width={40}
                tick={{ fontSize: 11 }}
                tickFormatter={(v) => `${v}%`}
              />
              <Tooltip
                formatter={(value, name) => [
                  value == null ? '—' : `${Number(value).toFixed(1)}%`,
                  name === 'all' ? 'All' : name === 'ios' ? 'iOS' : 'Android',
                ]}
                labelFormatter={(label) => `Day ${label}`}
              />
              <Legend
                formatter={(value) =>
                  value === 'all' ? 'All' : value === 'ios' ? 'iOS' : 'Android'
                }
              />
              <Line
                type="monotone"
                dataKey="all"
                name="all"
                stroke={PRIMARY_HEX}
                strokeWidth={2}
                dot={{ r: 2 }}
                connectNulls
              />
              <Line
                type="monotone"
                dataKey="ios"
                name="ios"
                stroke="#0ea5e9"
                strokeWidth={2}
                dot={{ r: 2 }}
                connectNulls
              />
              <Line
                type="monotone"
                dataKey="android"
                name="android"
                stroke="#f97316"
                strokeWidth={2}
                dot={{ r: 2 }}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <ChartCard
        title="Version mix over time (% of daily actives)"
        tooltip={HINTS.versionShare}
        height={320}
        loading={loading}
        error={q.error ? String(q.error) : undefined}
      >
        {!loading && !hasShare ? (
          <EmptyState title="No event data in window" />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={shareRows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis
                domain={[0, 100]}
                width={40}
                tick={{ fontSize: 11 }}
                tickFormatter={(v) => `${v}%`}
              />
              <Tooltip
                formatter={(value, name) => [
                  `${Number(value).toFixed(1)}%`,
                  versionLabel(String(name)),
                ]}
                labelFormatter={(label) => `Day ${label}`}
              />
              <Legend formatter={(value) => versionLabel(String(value))} />
              {versions.map((ver, idx) => (
                <Area
                  key={ver}
                  type="monotone"
                  dataKey={ver}
                  name={ver}
                  stackId="share"
                  stroke={VERSION_COLORS[idx % VERSION_COLORS.length]}
                  fill={VERSION_COLORS[idx % VERSION_COLORS.length]}
                  fillOpacity={0.55}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card padding="md" className="cursor-help" title={HINTS.distribution}>
          <h2 className="mb-1 text-sm font-semibold text-nova-foreground">
            Current distribution (all platforms)
          </h2>
          <p className="mb-3 text-xs text-nova-muted-foreground">
            Registered app_version on devices seen in the window
          </p>
          {loading ? (
            <p className="text-sm text-nova-muted-foreground">Loading…</p>
          ) : !hasDist ? (
            <EmptyState title="No active devices" />
          ) : (
            <BarList data={data?.distribution.all ?? []} />
          )}
        </Card>

        <Card padding="md" className="cursor-help" title={HINTS.behind}>
          <h2 className="mb-1 text-sm font-semibold text-nova-foreground">
            Distance from latest
          </h2>
          <p className="mb-3 text-xs text-nova-muted-foreground">
            Active devices vs configured latest thresholds
          </p>
          {loading ? (
            <p className="text-sm text-nova-muted-foreground">Loading…</p>
          ) : !hasDist ? (
            <EmptyState title="No active devices" />
          ) : (
            <BarList data={data?.behind_latest ?? []} />
          )}
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card padding="md" title={HINTS.distribution}>
          <h2 className="mb-3 text-sm font-semibold text-nova-foreground">iOS distribution</h2>
          {loading ? (
            <p className="text-sm text-nova-muted-foreground">Loading…</p>
          ) : (data?.distribution.ios.length ?? 0) === 0 ? (
            <EmptyState title="No iOS devices" />
          ) : (
            <BarList data={data?.distribution.ios ?? []} />
          )}
        </Card>
        <Card padding="md" title={HINTS.distribution}>
          <h2 className="mb-3 text-sm font-semibold text-nova-foreground">Android distribution</h2>
          {loading ? (
            <p className="text-sm text-nova-muted-foreground">Loading…</p>
          ) : (data?.distribution.android.length ?? 0) === 0 ? (
            <EmptyState title="No Android devices" />
          ) : (
            <BarList data={data?.distribution.android ?? []} />
          )}
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <ChartCard
            title="Days to upgrade"
            tooltip={HINTS.lagBuckets}
            height={280}
            loading={loading}
            error={q.error ? String(q.error) : undefined}
          >
            {!loading && !hasLag ? (
              <EmptyState title="No upgrades observed in window" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={(data?.upgrade_lag_buckets ?? []).map((b) => ({
                    label: b.key,
                    count: b.count,
                  }))}
                  margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis
                    allowDecimals={false}
                    width={40}
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => formatInt(Number(v))}
                  />
                  <Tooltip
                    formatter={(value) => [formatInt(Number(value)), 'Upgrades']}
                  />
                  <Bar dataKey="count" fill={PRIMARY_HEX} radius={[4, 4, 0, 0]} maxBarSize={56} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </div>

        <Card padding="md">
          <h2 className="mb-3 text-sm font-semibold text-nova-foreground">Upgrade lag by platform</h2>
          {loading ? (
            <p className="text-sm text-nova-muted-foreground">Loading…</p>
          ) : !hasLag ? (
            <EmptyState title="No upgrades observed" />
          ) : (
            <ul className="space-y-4">
              {(['ios', 'android'] as const).map((platform) => {
                const stats = data?.upgrade_lag_by_platform[platform];
                return (
                  <li key={platform} className="rounded-lg bg-nova-muted px-3 py-3">
                    <p className="text-xs font-medium uppercase tracking-wide text-nova-muted-foreground">
                      {platform === 'ios' ? 'iOS' : 'Android'}
                    </p>
                    <p className="mt-1 text-lg font-semibold tabular-nums text-nova-foreground">
                      {formatDays(stats?.median_days)}{' '}
                      <span className="text-sm font-normal text-nova-muted-foreground">median</span>
                    </p>
                    <p className="mt-0.5 text-xs text-nova-muted-foreground">
                      P90 {formatDays(stats?.p90_days)} · {formatInt(stats?.count ?? 0)} upgrades
                    </p>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/admin/app-versions')({
  validateSearch: (raw: Record<string, unknown>): Search => {
    const r = raw.range;
    if (r === '24h' || r === '7d' || r === '30d' || r === '90d')
      return { range: r };
    return { range: '30d' };
  },
  component: AppVersionsPage,
});
